import { Button } from '@/components/ui/button';
import { useMobileToken } from '@/hooks/use-mobile-token';
import { useAuthClient } from '@/lib/authClient';
import { getFrontendEnvVariables } from '@mls-wallet/env';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { useDeviceConnection } from '@/hooks/use-device-connection';
import denApi from '@/lib/api/denApi';

export function RegisterDevicePage() {
  const { user, signOut } = useAuthClient();
  const env = getFrontendEnvVariables();
  const { token, error, isLoading } = useMobileToken();
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [isConnecting, setIsConnecting] = useState(false);
  const { refetch } = useDeviceConnection({ refreshInterval: 5000 });

  const isDevEnv = env.ENVIRONMENT === 'LOCAL';

  useEffect(() => {
    // Generate QR code with the current page URL
    const currentUrl = `${env.PRODUCTION_URL}/register-device`;
    const qrURL = new URL(currentUrl);
    qrURL.searchParams.set('token', token);
    qrURL.searchParams.set('environment', env.ENVIRONMENT);

    QRCode.toDataURL(qrURL.href)
      .then((url: string) => {
        setQrCodeDataUrl(url);
      })
      .catch((error) => {
        console.error('Error generating QR code', error);
        setQrCodeDataUrl('');
      });
  }, [token, env]);

  return (
    <div className="flex bg-white flex flex-col items-center p-4 h-full">
      <div className="flex flex-col text-center w-full justify-between h-full">
        <div className="text-black mb-8 space-y-2">
          <p>Open the Den mobile app and start setting up your mobile app.</p>
          <p className="text-sm text-gray-600">
            Copy the token below into your mobile app.
          </p>
        </div>

        <div className="flex flex-col items-center gap-1 mb-8">
          {error ? (
            <div className="text-red-600">{String(error)}</div>
          ) : isLoading ? (
            <div className="w-80 h-80 border border-gray-200 rounded-lg bg-gray-100 flex items-center justify-center">
              <div className="text-gray-500">Generating mobile token...</div>
            </div>
          ) : (
            <img
              src={qrCodeDataUrl}
              alt="QR Code for device setup"
              className="w-80 h-80"
            />
          )}
          <p className="text-sm text-gray-600">{`not ${user?.email}?`}</p>
          <Button size="sm" onClick={() => signOut()}>
            Sign out
          </Button>
        </div>

        <p className="text-sm text-gray-600">
          This page will automatically update once you connect the Den mobile
          app.
        </p>

        {isDevEnv && (
          <div className="mt-6">
            <Button
              variant="secondary"
              disabled={isConnecting}
              onClick={async () => {
                try {
                  setIsConnecting(true);
                  // Mocking mobile connecting flow. This will be removed in the future
                  await denApi.post<void, void>(
                    `mock/mobile/connect`,
                    undefined
                  );
                  // Immediately revalidate the device status; background polling continues every 5s
                  await refetch();
                } catch (e: any) {
                  console.error('Mock connect failed', e);
                  alert('Failed to mock connect. Check console for details.');
                } finally {
                  setIsConnecting(false);
                }
              }}
            >
              Mock mobile connected (dev)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
